import { useEffect, useMemo, useRef, useState } from "react";
import {
  Award,
  ChevronDown,
  ChevronRight,
  Eye,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { TailSpin } from "react-loader-spinner";

import { Button } from "../../../../ui/button/button.tsx";
import { Checkbox } from "../../../../ui/checkbox/checkbox.tsx";
import {
  DatePicker,
  DatePickerFieldTrigger,
} from "../../../../ui/date-picker/date-picker.tsx";
import { FileField } from "../../../../ui/file-field/file-field.tsx";
import { TextField } from "../../../../ui/text-field/text-field.tsx";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { CertificationDocumentViewer } from "../../../certifications/certification-document-viewer.tsx";
import { useCertificationDocument } from "../../../certifications/use-certification-document.ts";
import { useEmployeeCertifications } from "../hooks/use-employee-certifications.ts";
import type {
  CertificationCatalogItem,
  CertificationCategory,
  EmployeeCertification,
} from "../types.ts";

import styles from "../employee-certifications.module.css";

type EmployeeCertificationsProps = {
  accessToken?: string;
};

function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatDateOnly(value: Date | null): string {
  if (!value) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function certificateNumberDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function formatApiLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function emptyCertification(
  item: CertificationCatalogItem,
  category: CertificationCategory,
): EmployeeCertification {
  return {
    certificationId: item.certificationId,
    category: category.category,
    certification: item.certification,
    issuedBy: "",
    certificateNumber: "",
    issueDate: "",
    expiryDate: "",
    certificateUrl: "",
    verified: false,
  };
}

export function EmployeeCertifications({
  accessToken,
}: EmployeeCertificationsProps) {
  const { data, categories, loading, saving, error, save, remove } =
    useEmployeeCertifications(accessToken);
  const certificateDocument = useCertificationDocument(accessToken);
  const [certifications, setCertifications] = useState<
    EmployeeCertification[]
  >([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [certificateFiles, setCertificateFiles] = useState<
    Record<number, File | null>
  >({});
  const submittingRef = useRef(false);

  useEffect(() => {
    const next = data?.certifications ?? [];
    setCertifications(next);
    setExpandedId(next[0]?.certificationId ?? null);
    setCertificateFiles({});
  }, [data]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  useEffect(() => {
    if (
      categories.length > 0 &&
      !categories.some((category) => category.category === activeCategory)
    ) {
      setActiveCategory(categories[0].category);
    }
  }, [activeCategory, categories]);

  const selectedIds = useMemo(
    () => new Set(certifications.map((item) => item.certificationId)),
    [certifications],
  );

  const persistedIds = useMemo(
    () =>
      new Set(
        (data?.certifications ?? []).map((item) => item.certificationId),
      ),
    [data],
  );

  const visibleCatalog = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query) {
      return categories.flatMap((category) =>
        category.certifications
          .filter((item) => item.certification.toLowerCase().includes(query))
          .map((item) => ({ item, category })),
      );
    }

    const category = categories.find(
      (item) => item.category === activeCategory,
    );
    return (category?.certifications ?? []).map((item) => ({
      item,
      category: category as CertificationCategory,
    }));
  }, [activeCategory, categories, search]);

  const toggleCertification = (
    item: CertificationCatalogItem,
    category: CertificationCategory,
  ) => {
    if (selectedIds.has(item.certificationId)) {
      removeCertificationLocally(item.certificationId);
      return;
    }

    setCertifications((current) => [
      ...current,
      emptyCertification(item, category),
    ]);
    setExpandedId(item.certificationId);
  };

  const removeCertificationLocally = (certificationId: number) => {
    setCertifications((current) =>
      current.filter((item) => item.certificationId !== certificationId),
    );
    setExpandedId((current) =>
      current === certificationId ? null : current,
    );
    setCertificateFiles((current) => {
      const next = { ...current };
      delete next[certificationId];
      return next;
    });
  };

  const deleteCertification = async (item: EmployeeCertification) => {
    if (!persistedIds.has(item.certificationId)) {
      removeCertificationLocally(item.certificationId);
      return;
    }

    if (item.employeeCertificationId == null) {
      showError("Unable to delete certification: employee certification ID is missing.");
      return;
    }

    setDeletingIds((current) => new Set(current).add(item.certificationId));
    try {
      await remove(item.employeeCertificationId);
      removeCertificationLocally(item.certificationId);
      showSuccess("Certification deleted successfully.");
    } catch {
      showError("Unable to delete certification.");
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(item.certificationId);
        return next;
      });
    }
  };

  const updateCertification = (
    certificationId: number,
    field: keyof EmployeeCertification,
    value: string,
  ) => {
    setCertifications((current) =>
      current.map((item) =>
        item.certificationId === certificationId
          ? { ...item, [field]: value }
          : item,
      ),
    );
  };

  const resetForm = () => {
    const next = data?.certifications ?? [];
    setCertifications(next);
    setExpandedId(next[0]?.certificationId ?? null);
    setCertificateFiles({});
  };

  const handleSave = async () => {
    if (submittingRef.current) return;
    const missingRequired = certifications.find(
      (item) => !item.certificateNumber.trim() || !item.issueDate,
    );
    if (missingRequired) {
      setExpandedId(missingRequired.certificationId);
      showError(
        `Certificate Number and Issue Date are required for ${missingRequired.certification}.`,
      );
      return;
    }
    const invalidCertificateNumber = certifications.find(
      (item) => !/^\d+$/.test(item.certificateNumber),
    );
    if (invalidCertificateNumber) {
      setExpandedId(invalidCertificateNumber.certificationId);
      showError(
        `Certificate Number must contain numbers only for ${invalidCertificateNumber.certification}.`,
      );
      return;
    }

    submittingRef.current = true;
    try {
      await save({
        certifications: certifications.map(
          ({ certificationId, certificateNumber, issueDate, expiryDate }) => ({
            certificationId,
            certificateNumber,
            issueDate,
            expiryDate,
            document: certificateFiles[certificationId] ?? null,
          }),
        ),
      });
      showSuccess("Certifications updated successfully.");
    } catch {
      showError("Unable to update certifications.");
    } finally {
      submittingRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <TailSpin
          height="42"
          width="42"
          color="#2563eb"
          ariaLabel="Loading certifications"
        />
      </div>
    );
  }

  return (
    <section className={styles.root} aria-labelledby="certifications-heading">
      <header className={styles.header}>
        <div className={styles.heading}>
          <span className={styles.headingIcon}>
            <Award size={22} aria-hidden />
          </span>
          <div>
            <h2 id="certifications-heading">Certifications</h2>
            <p>Add and manage your professional certifications</p>
          </div>
        </div>
        <label className={styles.search}>
          <Search size={17} aria-hidden />
          <span className="visually-hidden">Search certifications</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search certifications..."
          />
        </label>
      </header>

      <div className={styles.selector}>
        <aside className={styles.categories}>
          {categories.map((category) => (
            <button
              type="button"
              key={category.category}
              className={
                category.category === activeCategory && !search
                  ? styles.categoryActive
                  : styles.category
              }
              onClick={() => {
                setSearch("");
                setActiveCategory(category.category);
              }}
            >
              <span className={styles.categoryMark}>
                {category.category.slice(0, 1).toUpperCase()}
              </span>
              <span>{formatApiLabel(category.category)}</span>
            </button>
          ))}
        </aside>

        <section className={styles.catalog} aria-label="Certification catalog">
          <h3>Certifications</h3>
          <p>
            {search
              ? "Search results"
              : categories.find(
                  (category) => category.category === activeCategory,
                )?.category
                ? formatApiLabel(activeCategory)
                : ""}
          </p>
          <div className={styles.catalogList}>
            {visibleCatalog.map(({ item, category }) => (
              <div className={styles.catalogItem} key={item.certificationId}>
                <Checkbox
                  id={`certification-option-${item.certificationId}`}
                  label={item.certification}
                  checked={selectedIds.has(item.certificationId)}
                  onChange={() => toggleCertification(item, category)}
                />
              </div>
            ))}
            {visibleCatalog.length === 0 ? (
              <p className={styles.empty}>No certifications found.</p>
            ) : null}
          </div>
        </section>

        <section className={styles.selected} aria-label="Selected certifications">
          <div className={styles.selectedHeader}>
            <h3>Selected Certifications ({certifications.length})</h3>
            <button
              type="button"
              onClick={() => {
                setCertifications([]);
                setExpandedId(null);
                setCertificateFiles({});
              }}
              disabled={certifications.length === 0}
            >
              Clear All
            </button>
          </div>
          <div className={styles.chips}>
            {certifications.map((item) => (
              <span className={styles.chip} key={item.certificationId}>
                {item.certification}
                <button
                  type="button"
                  onClick={() =>
                    removeCertificationLocally(item.certificationId)
                  }
                  aria-label={`Remove ${item.certification}`}
                >
                  <X size={14} aria-hidden />
                </button>
              </span>
            ))}
          </div>
        </section>
      </div>

      <section className={styles.details} aria-labelledby="details-heading">
        <h3 id="details-heading">Certification Details</h3>
        {certifications.length === 0 ? (
          <p className={styles.detailsEmpty}>
            Select a certification to enter its details.
          </p>
        ) : (
          certifications.map((item) => {
            const expanded = expandedId === item.certificationId;
            return (
              <article className={styles.detailCard} key={item.certificationId}>
                <div className={styles.detailHeader}>
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={() =>
                      setExpandedId(expanded ? null : item.certificationId)
                    }
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDown size={18} aria-hidden />
                    ) : (
                      <ChevronRight size={18} aria-hidden />
                    )}
                    {item.certification}
                  </button>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => deleteCertification(item)}
                    disabled={deletingIds.has(item.certificationId)}
                    aria-label={`Delete ${item.certification}`}
                    title="Delete certification"
                  >
                    <Trash2 size={17} aria-hidden />
                  </button>
                </div>
                {expanded ? (
                  <div className={styles.fields}>
                    <TextField
                      id={`certificate-number-${item.certificationId}`}
                      label="Certificate Number"
                      required
                      inputMode="numeric"
                      pattern="[0-9]+"
                      value={item.certificateNumber}
                      error={
                        item.certificateNumber &&
                        !/^\d+$/.test(item.certificateNumber)
                          ? "Certificate Number must contain numbers only."
                          : undefined
                      }
                      onChange={(event) =>
                        updateCertification(
                          item.certificationId,
                          "certificateNumber",
                          certificateNumberDigits(event.target.value),
                        )
                      }
                    />
                    <DatePicker
                      id={`certificate-issue-date-${item.certificationId}`}
                      label="Issue Date"
                      required
                      selected={parseDateOnly(item.issueDate)}
                      onChange={(date) =>
                        updateCertification(
                          item.certificationId,
                          "issueDate",
                          formatDateOnly(date),
                        )
                      }
                      dateFormat="dd-MMM-yyyy"
                      customInput={
                        <DatePickerFieldTrigger placeholderText="Select issue date" />
                      }
                    />
                    <DatePicker
                      id={`certificate-expiry-date-${item.certificationId}`}
                      label="Expiry Date"
                      selected={parseDateOnly(item.expiryDate)}
                      minDate={parseDateOnly(item.issueDate) ?? undefined}
                      onChange={(date) =>
                        updateCertification(
                          item.certificationId,
                          "expiryDate",
                          formatDateOnly(date),
                        )
                      }
                      dateFormat="dd-MMM-yyyy"
                      isClearable
                      customInput={
                        <DatePickerFieldTrigger placeholderText="Select expiry date" />
                      }
                    />
                    <div className={styles.fileField}>
                      <FileField
                        id={`certificate-file-${item.certificationId}`}
                        label="Upload Certificate"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        hint="PDF, PNG, JPG or JPEG"
                        stagedFile={
                          certificateFiles[item.certificationId] ?? null
                        }
                        onChange={(file) =>
                          setCertificateFiles((current) => ({
                            ...current,
                            [item.certificationId]: file,
                          }))
                        }
                      />
                      {item.documentPath ? (
                        <Button
                          variant="muted-outline"
                          startIcon={<Eye size={16} aria-hidden />}
                          onClick={() => void certificateDocument.view(item.documentPath!)}
                          disabled={certificateDocument.loadingPath === item.documentPath}
                        >
                          {certificateDocument.loadingPath === item.documentPath
                            ? "Opening..."
                            : "View Uploaded Certificate"}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </section>

      <footer className={styles.footer}>
        <Button variant="light-grey" onClick={resetForm} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || certifications.length === 0}
        >
          {saving ? "Saving..." : "Save Certifications"}
        </Button>
      </footer>
      <CertificationDocumentViewer
        document={certificateDocument.document}
        onClose={certificateDocument.close}
      />
    </section>
  );
}
